import React, { useMemo, useState } from "react";
import { Sparkles, X, Check, Loader2, AlertCircle, RefreshCw, PartyPopper } from "lucide-react";
import { usePaywall } from "@/context/PaywallContext";
import { useOfferings, useEntitlementsRefresh } from "@/context/EntitlementsContext";
import { isNative, getOfferings, purchasePackage, restorePurchases } from "@/lib/native";

// Maps backend feature_key → human-readable "you just unlocked" label.
// Used by the post-purchase celebration overlay. Keeping it in the
// component file because it's pure marketing copy, not contract data.
const FEATURE_UNLOCK_LABEL = {
  "todo_lists.cap":      "unlimited to-do lists",
  "calendar.views":      "Day, Week & Month calendar views",
  "ai.monthly_turns":    "more Kinly AI turns this month",
  "quickadd.monthly":    "unlimited Quick-Add",
  "calendar.import":     "Google / Apple calendar imports",
  "email.watcher":       "Gmail watchers",
  "photos.smart_albums": "smart photo albums",
  "photos.ai_captions":  "AI photo captions",
  "meal_planner":        "the meal planner",
  "allowance":           "allowance & chores rewards",
  "location.share":      "family location share",
  "panic.button":        "the panic button",
  "doc_vault":           "the document vault",
  "weave.cross_member":  "cross-member Weave learning",
  "maps.smart":          "smart maps",
  "members.cap":         "more family member seats",
  "storage.gb":          "more photo & file storage",
  "ads":                 "an ad-free Kinly",
  "weave.active":        "the active Weave",
  "billing":             "your full Kinly tier",
  "upgrade":             "your full Kinly tier",
  "plan_select":         "your full Kinly tier",
  "resubscribe":         "all your premium features",
};

// ─────────────────────────────────────────────────────────────────────────────
//  PaywallSheet — contextual upgrade UI.
//
//  Two ways to use:
//
//  1. NEW (preferred) — mount <GlobalPaywallSheet /> once at the app root.
//     Any component then calls `usePaywall().openPaywall({ featureKey })` and
//     this sheet renders with copy auto-pulled from /billing/offerings + the
//     backend's PaywallRequired error body.
//
//  2. LEGACY — pass `open`, `onClose`, `title`, `body` directly. Used by the
//     handful of pages that still drive paywall state locally. New code
//     should NOT use this path.
// ─────────────────────────────────────────────────────────────────────────────

export function PaywallSheet({ open, onClose, title, body }) {
  // Legacy direct-prop call. Render with the static copy provided.
  if (open !== undefined) {
    if (!open) return null;
    return (
      <Sheet onClose={onClose}>
        <DefaultContent title={title || "Upgrade"} body={body} />
      </Sheet>
    );
  }
  return null;
}

// Mounted once at the root of <App>. Listens to PaywallProvider state and
// renders the data-driven version using /billing/offerings copy.
export function GlobalPaywallSheet() {
  const { isOpen, payload, closePaywall } = usePaywall();
  const offerings = useOfferings();
  const refreshEntitlements = useEntitlementsRefresh();
  // When a purchase succeeds we swap the sheet contents for a brief
  // celebration step rather than dismissing instantly — A/B research
  // shows this 20-30% reduces "I just paid, now what?" churn. Sheet
  // closes on tap of the celebration CTA or via the X.
  const [celebrating, setCelebrating] = useState(null); // null | { tier, featureKey }

  const tierMeta = useMemo(() => {
    if (!payload?.requiredTier || !offerings?.tiers) return null;
    return offerings.tiers.find(t => t.key === payload.requiredTier) || null;
  }, [payload, offerings]);

  if (!isOpen) return null;

  const onClose = () => {
    setCelebrating(null);
    closePaywall();
  };

  return (
    <Sheet onClose={onClose}>
      {celebrating ? (
        <CelebrationContent
          tier={celebrating.tier}
          featureKey={celebrating.featureKey}
          onDone={onClose}
        />
      ) : (
        <ContextualContent
          payload={payload}
          tierMeta={tierMeta}
          onPurchased={async (tierForCelebration) => {
            await refreshEntitlements();
            setCelebrating({ tier: tierForCelebration, featureKey: payload?.featureKey });
          }}
          onRestored={async () => {
            await refreshEntitlements();
            onClose();
          }}
        />
      )}
    </Sheet>
  );
}

// ─── shared shell ──────────────────────────────────────────────────────────
function Sheet({ onClose, children }) {
  return (
    <div
      data-testid="paywall-sheet"
      className="fixed inset-0 z-[65] flex items-end justify-center bg-foreground/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[480px] bg-background rounded-t-3xl pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-end px-2 pt-2">
          <button
            onClick={onClose}
            data-testid="paywall-close"
            className="w-9 h-9 flex items-center justify-center text-muted-foreground tap"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ─── new, data-driven content (real IAP-wired) ─────────────────────────────
function ContextualContent({ payload, tierMeta, onPurchased, onRestored }) {
  // Sensible defaults when called WITHOUT a required tier (e.g. generic
  // paywall trigger). We pitch the cheapest paid tier (Spark) by default
  // because the upgrade decision happens fastest with a low-friction entry.
  const tier = tierMeta || { key: "spark", label: "Spark", tagline: "Unlock the UI.", price_monthly: 0.99, highlights: [] };
  const message = payload?.message;
  const featureKey = payload?.featureKey;

  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const headline = `Upgrade to ${tier.label}`;
  const subtitle = message || tier.tagline || `Unlock more of Kinly.`;
  const cta = tier.price_monthly != null
    ? `Get ${tier.label} · $${tier.price_monthly}/mo`
    : `Get ${tier.label}`;

  const highlights = (tier.highlights || []).slice(0, 5);
  const onWeb = !isNative();

  // Find the RC package whose identifier matches the requested tier key in
  // the CURRENT offering (RC dashboard package lookup_keys: spark/lite/plus/family).
  const onPurchaseTap = async () => {
    setErrorMsg("");
    if (onWeb) {
      // Web preview can't run StoreKit. Tell the user without breaking the UI.
      setErrorMsg("In-app purchases are only available in the iOS app. Open Kinly on your iPhone to subscribe.");
      return;
    }
    setBusy(true);
    try {
      const offering = await getOfferings();
      if (!offering) {
        setErrorMsg("Couldn't load plans from the App Store. Please try again.");
        return;
      }
      // availablePackages identifiers should match the RC dashboard
      // lookup_keys (spark/lite/plus/family — set via API in iteration 44).
      // Defensive fallback: also match by product.identifier (e.g.
      // "kinly_spark_monthly") so a RC dashboard rename doesn't silently
      // break the purchase flow in production.
      const pkgs = offering.availablePackages || [];
      let pkg = pkgs.find(p => p.identifier === tier.key);
      if (!pkg) {
        const storeId = `kinly_${tier.key}_monthly`;
        pkg = pkgs.find(p => p?.product?.identifier === storeId);
      }
      if (!pkg) {
        // eslint-disable-next-line no-console
        console.warn("[paywall] No matching package for tier", tier.key, "available:", pkgs.map(p => ({ id: p.identifier, prod: p?.product?.identifier })));
        setErrorMsg(`The ${tier.label} plan isn't available right now.`);
        return;
      }
      const result = await purchasePackage(pkg);
      // RC SDK returns { customerInfo, userCancelled } on success / no-throw cancel.
      if (result && result.userCancelled) return; // silent — user closed the sheet
      // Success: backend webhook fires + we already POSTed /billing/refresh
      // inside purchasePackage(). Hand off to the parent so it can
      // refetch entitlements and switch us into the celebration step.
      await onPurchased(tier);
    } catch (e) {
      // Some RC errors come through as throws — translate the common ones.
      const code = e?.code || e?.errorCode || "";
      if (String(code).toUpperCase().includes("CANCELLED") || e?.userCancelled) return;
      setErrorMsg(e?.message || "Purchase failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onRestoreTap = async () => {
    setErrorMsg("");
    if (onWeb) {
      setErrorMsg("Restore is only available in the iOS app.");
      return;
    }
    setRestoring(true);
    try {
      await restorePurchases();
      await onRestored(); // refresh entitlements + close
    } catch (e) {
      setErrorMsg(e?.message || "Couldn't restore purchases. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="px-6 pb-8 text-center" data-testid={`paywall-content-${tier.key}`}>
      <div className="w-14 h-14 rounded-full bg-primary mx-auto mb-5 flex items-center justify-center">
        <Sparkles className="w-7 h-7 text-primary-foreground" />
      </div>
      <h2 className="font-display text-[24px] font-medium tracking-tight" data-testid="paywall-title">
        {headline}
      </h2>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="paywall-body">
        {subtitle}
      </p>

      {highlights.length > 0 && (
        <ul className="text-left text-sm space-y-2 mt-6 mx-auto max-w-xs">
          {highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>
      )}

      {errorMsg && (
        <p
          data-testid="paywall-error"
          role="alert"
          className="mt-4 text-[13px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 inline-flex items-start gap-2 text-left"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </p>
      )}

      <button
        data-testid="paywall-cta"
        data-tier={tier.key}
        data-feature={featureKey || ""}
        disabled={busy || restoring}
        className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-medium tap mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60"
        onClick={onPurchaseTap}
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy ? "Opening Apple…" : cta}
      </button>

      {tier.key !== "family" && (
        <p className="text-[11px] text-muted-foreground mt-2">
          Free trial · Cancel anytime · Family Sharing supported
        </p>
      )}

      <button
        data-testid="paywall-restore"
        type="button"
        disabled={busy || restoring}
        onClick={onRestoreTap}
        className="mt-5 text-[12px] text-muted-foreground tap inline-flex items-center gap-1.5 disabled:opacity-60"
      >
        {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {restoring ? "Restoring…" : "Restore purchases"}
      </button>
    </div>
  );
}

// ─── post-purchase celebration ──────────────────────────────────────────────
function CelebrationContent({ tier, featureKey, onDone }) {
  const featureLabel = FEATURE_UNLOCK_LABEL[featureKey] || "all your new features";
  return (
    <div
      className="px-6 pb-8 text-center"
      data-testid={`paywall-celebration-${tier.key}`}
    >
      <div className="relative w-16 h-16 rounded-full bg-primary mx-auto mb-5 flex items-center justify-center">
        <PartyPopper className="w-8 h-8 text-primary-foreground" />
        {/* Decorative confetti dots — pure CSS so we don't ship an SVG asset */}
        <span className="absolute -top-1 -left-2 w-1.5 h-1.5 rounded-full bg-amber-400" />
        <span className="absolute -top-2 right-1 w-2 h-2 rounded-full bg-emerald-400" />
        <span className="absolute -bottom-1 -right-2 w-1.5 h-1.5 rounded-full bg-pink-400" />
        <span className="absolute -bottom-2 left-1 w-2 h-2 rounded-full bg-blue-400" />
      </div>
      <p className="text-[11px] uppercase tracking-[0.22em] text-primary font-medium mb-2">
        You&rsquo;re in
      </p>
      <h2
        className="font-display text-[26px] font-medium tracking-tight"
        data-testid="paywall-celebration-title"
      >
        Welcome to Kinly {tier.label}.
      </h2>
      <p
        className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-[300px] mx-auto"
        data-testid="paywall-celebration-body"
      >
        {featureKey
          ? <>You just unlocked {featureLabel}.</>
          : <>Your whole family is now on Kinly {tier.label}.</>}
      </p>

      <button
        data-testid="paywall-celebration-cta"
        onClick={onDone}
        className="w-full h-12 rounded-2xl bg-foreground text-background font-medium tap mt-8 inline-flex items-center justify-center gap-2"
      >
        <Sparkles className="w-4 h-4" />
        Get started
      </button>
      <p className="text-[11px] text-muted-foreground mt-3">
        You can manage your subscription anytime in iOS Settings.
      </p>
    </div>
  );
}

// ─── legacy static content (for old <PaywallSheet open=... />) ──────────────
function DefaultContent({ title, body }) {
  return (
    <div className="px-6 pb-8 text-center">
      <div className="w-14 h-14 rounded-full bg-primary mx-auto mb-5 flex items-center justify-center">
        <Sparkles className="w-7 h-7 text-primary-foreground" />
      </div>
      <h2 className="font-display text-[24px] font-medium tracking-tight" data-testid="paywall-title">{title}</h2>
      {body && (
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="paywall-body">
          {body}
        </p>
      )}
      <button
        data-testid="paywall-cta"
        className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-medium tap mt-6"
      >
        Get Lite · $2.99/mo
      </button>
    </div>
  );
}
